import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../loader/Loader";
import { Grid, Pagination, Typography } from "@mui/material";
import {
  getFilterOptions,
  createCartProductFromCatalogue,
  calculateTotalAmountAndQuantity,
  createQuery,
  getPieceFilterMapping,
} from "../../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { UOM } from "../../../features/createSecondaryOrder/CreateSecondaryOrderType";
import CatalogueFilters from "../../dms_common/CatalogueFilters";
import { OrderType, popupType } from "../../../types/types";
import { GenericPopUp } from "../../popup/genericPopUp";
import Search from "../../dms_common/Search";
import {
  getDataFromLocalDb,
  insertDataIntoLocalDB,
} from "../../../services/dbService";
import {
  Cart,
  Catalogue,
  CartProduct,
  ProductUI,
} from "../../../features/createSecondaryOrder/models";
import CatalogueProductCard from "../../createSecondaryOrder/CatalogueProductCard";
import { createCatalogue } from "../../../services/catalogueService";
import CartSummaryFooter from "../../dms_common/CartSummaryFooter";
import { useDispatch } from "react-redux";
import { setDiscountModalState } from "../../../stateManagement/actions/claims";
import NewDiscountModal from "../../../features/claimsNew/modals/NewDiscountModal";

const CreateRsuLoadout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedSku, setSelectedSku] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [productLoading, setProductLoading] = useState<boolean>(false);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [cart, setCart] = useState<Cart>({});
  const [catalogue, setCatalogue] = useState<Catalogue[]>([]);
  const [productUIList, setProductUIList] = useState<ProductUI[]>([]);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [filterOption, setFilterOption] = useState<Record<string, any>>({});
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalNumberOfProducts, setTotalNumberOfProducts] = useState(0);
  const productsPerPage = 18; // Number of products to display per page
  const [customerBillingInfo, setCustomerBillingInfo] = useState<
    Record<string, any>
  >({
    outletCode: "self",
    salesRep: "Kiran Kumar",
    customerChannel: "Groceries",
    shippingAddress: "Pritampura, Delhi",
    billDate: dayjs().format("DD/MM/YY"),
    deliveryDate: dayjs().add(2, "day").format("DD/MM/YY"),
    paymentMethod: "UPI",
  });

  const [genericModalState, setGenericModalState] = useState(false);
  const [genericModalMessage, setGenericModalSMessage] = useState("");
  const [genericModaltype, setGenericModalType] = useState<popupType>("Error");

  const openPopUp = (message: string, modalType: popupType) => {
    setGenericModalSMessage(message);
    setGenericModalState(true);
    setGenericModalType(modalType);
  };

  const updateQuantityInCart = async (
    id: string,
    newQuantity: number,
    type: string
  ) => {
    if (newQuantity >= 0) {
      const newCaseQuantity = type === UOM.CASE ? newQuantity : 0;
      const newPieceQuantity = type === UOM.PIECE ? newQuantity : 0;
      setCart((prevCart) => {
        const updatedCart = { ...prevCart };

        if (!updatedCart[customerBillingInfo?.outletCode]) {
          updatedCart[customerBillingInfo?.outletCode] = {
            productsMap: {},
            customer: customerBillingInfo,
          };
        }

        const customer = updatedCart[customerBillingInfo?.outletCode];

        const oldCaseQuantity = customer?.productsMap[id]?.caseQuantity || 0;
        const oldPieceQuantity = customer?.productsMap[id]?.pieceQuantity || 0;
        if (
          newQuantity === 0 &&
          (type === UOM.PIECE
            ? customer?.productsMap[id]?.caseQuantity === 0
            : customer?.productsMap[id]?.pieceQuantity === 0)
        ) {
          const { [id]: removedProduct, ...remainingProducts } =
            customer.productsMap;
          customer.productsMap = remainingProducts;
        } else {
          if (customer.productsMap[id]) {
            customer.productsMap[id] = {
              ...customer.productsMap[id],
              ...(type === "cs"
                ? { caseQuantity: newQuantity }
                : { pieceQuantity: newQuantity }),
            };
          } else {
            const product = createCartProductFromCatalogue(catalogue, id);
            if (product) {
              customer.productsMap[id] = {
                ...product,
                ...(type === "cs"
                  ? { caseQuantity: newQuantity }
                  : { pieceQuantity: newQuantity }),
              };
            }
          }
        }

        const { totalAmount: newTotalAmount, totalQuantity: newTotalQuantity } =
          calculateTotalAmountAndQuantity(
            totalQuantity,
            totalAmount,
            id,
            newCaseQuantity,
            oldCaseQuantity,
            newPieceQuantity,
            oldPieceQuantity,
            0,
            0,
            catalogue,
            UOM.CASE
          );
        setTotalAmount(newTotalAmount);
        setTotalQuantity(newTotalQuantity);
        insertDataIntoLocalDB("Cart", [{ id: 1, data: updatedCart }]);
        return updatedCart;
      });
    }
  };

  const handleCheckout = () => {
    if (
      Object.keys(cart?.[customerBillingInfo?.outletCode]?.productsMap ?? {})
        .length > 0
    ) {
      setCart((prevCart) => {
        const updatedCart = { ...prevCart };
        if (updatedCart[customerBillingInfo?.outletCode]) {
          updatedCart[customerBillingInfo?.outletCode] = {
            productsMap:
              updatedCart[customerBillingInfo?.outletCode].productsMap,
            customer: customerBillingInfo,
          };
        }
        insertDataIntoLocalDB("Cart", [{ id: 1, data: updatedCart }]);
        return updatedCart;
      });
    } else {
      openPopUp("Add items to the cart for proceeding further.", "Alert");
    }
  };

  useEffect(() => {
    const getAndSetData = async () => {
      setLoading(true);
      try {
        const catalogue = await createCatalogue(OrderType.PRIMARY);

        const responseFilterOPtion = await getFilterOptions(catalogue);
        if (responseFilterOPtion) {
          setFilterOption(responseFilterOPtion);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };
    getAndSetData();
    createProductUI();
  }, []);

  useEffect(() => {
    if (searchedValue.length > 3 || searchedValue.length == 0) {
      createProductUI();
    }
  }, [searchedValue, selectedOptions]);

  const updateFilterSelection = (filterType: string, option: string) => {
    setSelectedOptions((prevSelectedOptions) => {
      return {
        ...prevSelectedOptions,
        [filterType]:
          prevSelectedOptions?.[filterType] === option ? "ALL" : option,
      };
    });
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    createProductUI();
  }, [currentPage]);

  const createProductUI = async () => {
    setProductLoading(true);
    try {
      const query = createQuery(searchedValue, selectedOptions);
      const responseCatalogue = await getDataFromLocalDb(
        "PrimaryCatalogue",
        query,
        currentPage,
        productsPerPage
      );

      setTotalNumberOfProducts(responseCatalogue?.total);
      setCatalogue(responseCatalogue?.data);
      let amount = 0;
      let quantity = 0;

      const productUi: ProductUI[] = responseCatalogue?.data?.map(
        (catalogueItem: Catalogue) => {
          const product: ProductUI = {
            isFavourite: catalogueItem?.isFavourite,
            isRecommended: catalogueItem?.isRecommended,
            name: catalogueItem?.name,
            level: catalogueItem?.level,
            description: catalogueItem?.description,
            batchCode: catalogueItem?.batchCode,
            marketSkuCode: catalogueItem?.marketSkuCode,
            pieceQuantity: 0, // cartItem?.pieceQuantity || 0
            caseQuantity: 0, // cartItem?.caseQuantity || 0
            suggestedQuantity: catalogueItem?.suggestedQty,
            suggestedOtherQuantity: catalogueItem?.suggestedOtherQty,
            suggestedCaseQuantity: catalogueItem?.suggestedCaseQty,
            priority: catalogueItem?.priority,
            images: catalogueItem?.images,
            imageUrl: catalogueItem?.imageUrl,
            category: catalogueItem?.category,
            brand: catalogueItem?.brand,
            price: catalogueItem?.packPtr,
            casePrice: catalogueItem?.casePtr,
            amount: 0,
            margin: 0,
            netPtr: 0,
            maxDiscount: 0,
            volume: catalogueItem?.pieceSize,
            savings: 0,
            referenceNo: "",
            otherUnitToPieceQuantity: catalogueItem?.otherUnitToPieceQuantity,
            caseMrp: catalogueItem?.caseMrp,
            pieceSize: catalogueItem?.pieceSize,
            otherUnitMrp: 0,
            time: "",
            otherUnitPrice: catalogueItem?.otherUnitMrp,
            qtyUnit: "",
            otherUnitQuantity: 0,
            mrp: catalogueItem?.mrp,
            productTag: catalogueItem?.productTag,
            outletCode: "",
            caseToPieceQty: catalogueItem?.case2Piece,
            feature: "",
            underperformanceAttr: "",
            searchIndex: catalogueItem?.searchIndex,
            supplier: catalogueItem?.supplier,
            actualPtr: 0,
            isDiscountAvailable: false,
            tax: 0,
            pieceToOtherUnitQuantity: 0,
            otherUnitName: catalogueItem?.otherUnitName,
            additionalSchemeDescription: "",
            smartBuy: "",
            isUpdated: false,
            subCategory: "",
            isUOMChanged: false,
            refId: "",
            stockQty: 0,
            groupIdV1: "",
            maxBenefitStringV1: "",
            recommendedAttribures: catalogueItem?.recommendedAttributesV2,
            itemDescV1: "",
            uom: catalogueItem?.uom,
            typeV1: "",
            bandedMaxQuantityV1: 0,
            minQtyForEarnPoints: 0,
            recommendationId: catalogueItem?.recommendationId,
            recommendationPriority: catalogueItem?.recommendationPriority,
            productCode: catalogueItem?.productCode,
            productDescription: catalogueItem?.productDescription,
            creditQty: 0,
            srp: 0,
            returnCode: catalogueItem?.returnCode,
            discountMargin: 0,
            eb2bCode: catalogueItem?.eb2bCode,
            secondaryProductTag: catalogueItem?.secondaryProductTag,
            outOfStock: catalogueItem?.outOfStock,
            isNotify: catalogueItem?.isNotify,
            sbu: catalogueItem?.sbu,
            eanCode: catalogueItem?.eanCode,
            availableCaseQty: catalogueItem?.availableCaseQty,
            availablePieceQty: catalogueItem?.availablePieceQty,
            availableOtherQty: catalogueItem?.availableOtherQty,
            packPtr: catalogueItem?.packPtr,
            casePtr: catalogueItem?.casePtr,
          };
          return product;
        }
      );

      setTotalAmount(amount);
      setTotalQuantity(quantity);
      setProductUIList(productUi);
      setProductLoading(false);
    } catch (error) {
      setProductLoading(false);
      console.error(error);
    }
  };

  const getSkuData = () => {
    return {
      productUI: selectedSku,
      orderDetails: [
        {
          extendedAttributes: { blobKey: selectedSku?.imageUrl },
          skuDescription: selectedSku?.name,
          skuCode: selectedSku?.batchCode,
          caseQuantity: selectedSku?.availableCaseQty,
          pieceQuantity: selectedSku?.availablePieceQty,
          discountInfo: selectedSku?.schemes?.map((scheme: any) => {
            return { ...scheme, discountInfo: scheme?.schemeDescription };
          }),
          mrp: selectedSku?.mrp,
          pieceSize: getPieceFilterMapping(selectedSku?.pieceSize?.toString()),
          productInfo: { skuDescription: selectedSku?.name ?? "" }
        },
      ],
    };
  };

  const handleClickViewSkuDetails = (product: ProductUI) => {
    setSelectedSku(product);
    dispatch(setDiscountModalState(true));
  };

  return (
    <div style={{ padding: 0 }} className="secondary_order_container">
      {loading ? (
        <Loader sx={{ position: "absolute", top: "45%" }} />
      ) : (
        <>
          <GenericPopUp
            type={genericModaltype}
            message={genericModalMessage}
            openGenericModal={genericModalState}
            setOpenGenericModal={() => {
              setGenericModalState(false);
            }}
          />
          <div
            style={{
              display: "flex",
              flex: "0 0 auto",
              marginTop: "10px",
              justifyContent: "space-between",
              margin: "10px 0",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: "#5C5C5C",
                fontWeight: 600,
                letterSpacing: 0,
                fontSize: "16px",
              }}
            >
              All products{" "}
              <span
                style={{ color: "#7C7C7C", fontWeight: 400, fontSize: "14px" }}
              >
                ({totalNumberOfProducts})
              </span>
            </Typography>
            <div className="secondary-order-search-bar">
              <Search searchFunction={setSearchedValue} />
            </div>
          </div>

          <CatalogueFilters
            filterOptions={filterOption}
            updateFilterSelection={updateFilterSelection}
            selectedFilters={selectedOptions}
          />

          <Grid
            container
            spacing={4}
            className="product_selection_and_filter_grid"
            sx={{
              flex: "1 1 auto",
              overflowY: "hidden",
              display: "flex",
              height: "77%",
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                overflowY: "hidden",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                flex: "1 1 auto",
              }}
            >
              <Grid
                item
                xs={12}
                className="product-selection-grid"
                sx={{ flex: "1 1 auto" }}
              >
                {productUIList?.length > 0 ? (
                  <>
                    <div
                      className="select-product "
                      style={{ gridTemplateColumns: "repeat(5, 1fr)" }}
                    >
                      {productLoading ? (
                        <Loader />
                      ) : (
                        productUIList?.map((productUI: ProductUI) => {
                          return (
                            <CatalogueProductCard
                              key={productUI?.batchCode}
                              productUI={productUI}
                              updateQuantityInCart={updateQuantityInCart}
                              handleClickViewSkuDetails={
                                handleClickViewSkuDetails
                              }
                              customerInfo={{ outletCode: "self" }}
                              orderType={OrderType.SECONDARY}
                              setCart={setCart}
                              catalogue={catalogue}
                            />
                          );
                        })
                      )}
                    </div>

                    <div
                      style={{
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex",
                      }}
                    >
                      <Pagination
                        count={Math.ceil(
                          totalNumberOfProducts / productsPerPage
                        )}
                        page={currentPage}
                        onChange={handleChangePage}
                        variant="outlined"
                        color="primary"
                        style={{ margin: "10px" }}
                      />
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                      height: "100%",
                    }}
                  >
                    <p>No products found</p>
                  </div>
                )}
              </Grid>

              <CartSummaryFooter
                totalAmountAndQtyInfo={{
                  numberOfProducts: 0,
                  totalCaseQuantity: 0,
                  totalPieceQuantity: 0,
                  totalOtherUnitQuantity: 0,
                  totalAmount: 0,
                }}
                cartProducts={null}
                onButtonClick={() => navigate("review")}
                buttonText={"Review Loadout"}
                loadout={true}
                disabled={true}
                reviewLoadout={false}
                endIcon={true}
                rsu={true}
              />
            </Grid>
          </Grid>
        </>
      )}
      <NewDiscountModal
        selectedSkuCode={selectedSku?.batchCode}
        skuData={getSkuData()}
        showFooter={false}
        qtyLabel="In Stock"
        showMrpAndPackSize={true}
      />
    </div>
  );
};

export default CreateRsuLoadout;
